<template>
    <div>
        <button
            name="change_payment_method"
            :class="[
                hasError(errors, 'payment_method_id')
                    ? 'text-red-900 border-red-600 shadow-lg'
                    : 'text-gray-700 border-gray-300 shadow-sm',
                'w-full inline-flex justify-center items-center px-6 py-3 my-3 border text-base font-medium rounded-md bg-white hover:bg-gray-50 focus:outline-none',
            ]"
            @click="open = true"
        >
            <PencilAltIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            {{ buttonText }}
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg mx-4 mt-5 mb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:m-6"
                        >
                            <form
                                ref="form"
                                action="/checkout/payment-method"
                                method="POST"
                                @submit.prevent="onSubmit()"
                            >
                                <input
                                    type="hidden"
                                    name="_method"
                                    value="PATCH"
                                />
                                <input
                                    type="hidden"
                                    name="_token"
                                    :value="csrf"
                                />
                                <input
                                    type="hidden"
                                    name="payment_method_id"
                                    :value="inputs.payment_method_id"
                                />

                                <div
                                    class="flex justify-between items-center px-4 py-5 sm:px-6 bg-gray-100 text-gray-800 rounded-t-lg"
                                >
                                    <div class="flex items-center">
                                        <CreditCardIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        <h3 class="font-bold">
                                            Payment Method
                                        </h3>
                                    </div>

                                    <button
                                        type="button"
                                        class="text-gray-400 hover:text-gray-500 focus:outline-none"
                                        @click="open = false"
                                    >
                                        <span class="sr-only">Close</span>
                                        <XIcon
                                            class="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>

                                <div
                                    v-if="canPlaceOfflineOrders"
                                    :class="[
                                        inputs.payment_type === 'card'
                                            ? 'pb-1 sm:pb-0'
                                            : 'pb-6',
                                        'px-4 pt-5 sm:px-6 sm:pt-6',
                                    ]"
                                >
                                    <label
                                        for="payment_type"
                                        class="block text-sm font-medium text-gray-700"
                                    >
                                        Payment Type
                                    </label>

                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <select
                                            v-model="inputs.payment_type"
                                            id="payment_type"
                                            name="payment_type"
                                            :class="[
                                                hasError(errors, 'payment_type')
                                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                    : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                            ]"
                                            :aria-invalid="
                                                hasError(errors, 'payment_type')
                                                    ? 'true'
                                                    : 'false'
                                            "
                                            :aria-describedby="
                                                hasError(errors, 'payment_type')
                                                    ? 'payment_type-error'
                                                    : ''
                                            "
                                        >
                                            <option
                                                v-for="paymentType in paymentTypes"
                                                :key="paymentType"
                                                :value="paymentType"
                                            >
                                                {{ ucwords(paymentType) }}
                                            </option>
                                        </select>
                                        <div
                                            v-if="
                                                hasError(errors, 'payment_type')
                                            "
                                            class="absolute pr-8 inset-y-0 right-0 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        v-if="hasError(errors, 'payment_type')"
                                        class="mt-2 text-sm text-red-600"
                                        id="payment_type-error"
                                    >
                                        {{ error(errors, "payment_type") }}
                                    </p>
                                </div>

                                <div
                                    v-if="inputs.payment_type === 'card'"
                                    class="px-4 pt-4 pb-6 sm:px-6 sm:pt-6"
                                >
                                    <div v-if="initialPaymentMethod">
                                        <RadioGroup
                                            v-model="inputs.payment_method_id"
                                        >
                                            <RadioGroupLabel class="sr-only">
                                                Payment Method
                                            </RadioGroupLabel>
                                            <div
                                                class="bg-white rounded-md -space-y-px"
                                            >
                                                <RadioGroupOption
                                                    :data-test="
                                                        'payment-method-' +
                                                        paymentMethod.id
                                                    "
                                                    as="template"
                                                    v-for="(
                                                        paymentMethod,
                                                        paymentMethodId
                                                    ) in paymentMethods"
                                                    :key="paymentMethod.id"
                                                    :value="paymentMethod.id"
                                                    v-slot="{ checked }"
                                                >
                                                    <div
                                                        :class="[
                                                            paymentMethodId ===
                                                            0
                                                                ? 'rounded-tl-md rounded-tr-md'
                                                                : '',
                                                            checked
                                                                ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                                                : 'border-gray-200',
                                                            'relative border p-4 flex cursor-pointer focus:outline-none',
                                                        ]"
                                                    >
                                                        <div
                                                            class="flex items-center"
                                                        >
                                                            <span
                                                                :class="[
                                                                    checked
                                                                        ? 'bg-nutreance-green border-transparent'
                                                                        : 'bg-white border-gray-300',
                                                                    'h-4 w-4 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                                                ]"
                                                                aria-hidden="true"
                                                            >
                                                                <span
                                                                    class="rounded-full bg-white w-1.5 h-1.5"
                                                                />
                                                            </span>
                                                        </div>

                                                        <div class="ml-3 flex">
                                                            <svg-vue
                                                                :icon="
                                                                    paymentMethodCardTypeSvgPath(
                                                                        paymentMethod,
                                                                    )
                                                                "
                                                                :key="
                                                                    paymentMethodCardType(
                                                                        paymentMethod,
                                                                    )
                                                                "
                                                                class="w-16 h-auto rounded shadow mr-3"
                                                                aria-hidden="true"
                                                            />

                                                            <div
                                                                class="flex flex-col"
                                                            >
                                                                <RadioGroupLabel
                                                                    as="span"
                                                                    :class="[
                                                                        checked
                                                                            ? 'text-emerald-700'
                                                                            : 'text-gray-500',
                                                                        'block text-sm',
                                                                    ]"
                                                                >
                                                                    Ending in
                                                                    {{
                                                                        paymentMethodCardNumber(
                                                                            paymentMethod,
                                                                        )
                                                                    }}
                                                                </RadioGroupLabel>
                                                                <RadioGroupDescription
                                                                    as="span"
                                                                    :class="[
                                                                        checked
                                                                            ? 'text-emerald-700'
                                                                            : 'text-gray-500',
                                                                        'block text-sm',
                                                                    ]"
                                                                >
                                                                    Expires
                                                                    {{
                                                                        paymentMethodExpiration(
                                                                            paymentMethod,
                                                                        )
                                                                    }}
                                                                </RadioGroupDescription>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </RadioGroupOption>

                                                <RadioGroupOption
                                                    id="use-new-payment-method"
                                                    as="template"
                                                    value=""
                                                    v-slot="{ checked }"
                                                >
                                                    <div
                                                        :class="[
                                                            paymentMethods.length ===
                                                            0
                                                                ? 'rounded-tl-md rounded-tr-md'
                                                                : '',
                                                            checked
                                                                ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                                                : 'border-gray-200',
                                                            'rounded-bl-md rounded-br-md relative border py-6 px-4 flex cursor-pointer focus:outline-none',
                                                        ]"
                                                    >
                                                        <span
                                                            :class="[
                                                                checked
                                                                    ? 'bg-nutreance-green border-transparent'
                                                                    : 'bg-white border-gray-300',
                                                                'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                                            ]"
                                                            aria-hidden="true"
                                                        >
                                                            <span
                                                                class="rounded-full bg-white w-1.5 h-1.5"
                                                            />
                                                        </span>

                                                        <div
                                                            class="ml-3 flex flex-col"
                                                        >
                                                            <RadioGroupLabel
                                                                as="span"
                                                                :class="[
                                                                    checked
                                                                        ? 'text-emerald-900'
                                                                        : 'text-gray-900',
                                                                    'block text-sm font-medium',
                                                                ]"
                                                            >
                                                                Use a New Card
                                                            </RadioGroupLabel>
                                                        </div>
                                                    </div>
                                                </RadioGroupOption>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div
                                        v-if="!inputs.payment_method_id"
                                        :class="[
                                            initialPaymentMethod
                                                ? 'pt-4 sm:pt-6'
                                                : '',
                                        ]"
                                    >
                                        <PaymentMethodFormFields
                                            @cardDataTokenized="
                                                onCardDataTokenized
                                            "
                                            @cardDataTokenizationFailed="
                                                onCardDataTokenizationFailed
                                            "
                                            :addresses="addresses"
                                            :canPlaceOfflineOrders="
                                                canPlaceOfflineOrders
                                            "
                                            :countries="countries"
                                            :defaultAddressId="defaultAddressId"
                                            :emailOptIn="emailOptIn"
                                            :errors="errors"
                                            :includeDefaultField="false"
                                            :includeVerifyField="
                                                includeVerifyField
                                            "
                                            :initialCountry="initialCountry"
                                            :initialStates="initialStates"
                                            :old="old"
                                            :paymentGateway="paymentGateway"
                                            :paymentMethod="
                                                initialPaymentMethod
                                            "
                                            :type="'create'"
                                        />
                                    </div>
                                </div>

                                <div
                                    class="px-4 pb-4 sm:px-6 sm:pb-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Save
                                    </button>
                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    CreditCardIcon,
    ExclamationCircleIcon,
    PencilAltIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import { useString } from "../../functions/useString";
import PaymentMethodFormFields from "../paymentMethods/PaymentMethodFormFields.vue";
import { DateTime } from "luxon";
import LoadingIcon from "../LoadingIcon.vue";
import type { AppEmitter } from "../../emitter";

const paymentTypes = ["card", "check", "wise"];

const props = withDefaults(
    defineProps<{
        addresses?: any;
        canPlaceOfflineOrders?: boolean;
        countries?: any;
        csrf?: string;
        emailOptIn?: string;
        errors?: any;
        includeVerifyField?: boolean;
        initialCountry?: any;
        initialPaymentMethod?: any;
        initialStates?: any;
        old?: any;
        paymentGateway?: any;
        paymentType?: string;
        paymentMethods?: any;
    }>(),
    {
        canPlaceOfflineOrders: false,
        emailOptIn: "1",
        includeVerifyField: false,
        paymentType: "card",
    },
);

const emitter = inject<AppEmitter>("mitt")!;
const { error, hasError, oldInput, submitted } = useForm();
const { ucwords } = useString();
const form = ref<any>(null);
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, "payment_method_id") &&
        !props.old.payment_method_id,
);

const buttonText = computed(() => {
    const paymentType = props.canPlaceOfflineOrders ? "Payment Method" : "Card";
    return props.initialPaymentMethod || props.paymentType !== "card"
        ? `Change ${paymentType}`
        : `Add a New ${paymentType}`;
});

const defaultAddressId = computed(() => {
    if (props.initialPaymentMethod) {
        for (const key in props.addresses) {
            if (
                Object.prototype.hasOwnProperty.call(props.addresses, key) &&
                props.addresses[key].id ===
                    props.initialPaymentMethod.address_id
            ) {
                return props.initialPaymentMethod.address_id.toString();
            }
        }
    }

    return props.addresses.length > 0 ? props.addresses[0].id.toString() : "";
});

const inputs = reactive<{
    payment_type: any;
    payment_method_id: number | string;
}>({
    payment_type: Object.prototype.hasOwnProperty.call(
        props.old,
        "payment_type",
    )
        ? oldInput(props.old, "payment_type")
        : props.paymentType,
    payment_method_id: Object.prototype.hasOwnProperty.call(
        props.old,
        "payment_method_id",
    )
        ? oldInput(props.old, "payment_method_id")
            ? parseInt(oldInput(props.old, "payment_method_id"))
            : ""
        : props.initialPaymentMethod
          ? parseInt(props.initialPaymentMethod.id)
          : "",
});

function paymentMethodCardNumber(paymentMethod: any) {
    return paymentMethod.card_number.replace(/X/g, "");
}

function paymentMethodCardType(paymentMethod: any) {
    return paymentMethod.card_type.toLowerCase().replace(/_/g, "-");
}

function paymentMethodCardTypeSvgPath(paymentMethod: any) {
    return `payments/${paymentMethod.card_type.toLowerCase().replace(/_/g, "-")}`;
}

function paymentMethodExpiration(paymentMethod: any) {
    return DateTime.fromISO(paymentMethod.expiration).toFormat("LL/yy");
}

function onSubmit() {
    if (submitted.value) {
        return;
    }

    submitted.value = true;

    if (inputs.payment_type === "card" && !inputs.payment_method_id) {
        emitter.emit("tokenizeCardData");
        return;
    }

    form.value.submit();
}

function onCardDataTokenized() {
    form.value.submit();
}

function onCardDataTokenizationFailed() {
    submitted.value = false;
}
</script>
